import type { ApiClient } from '@saas/ui';
import { coreApi } from './api.js';

/**
 * ブラウザ側の Web Push（03 §11.3c・04 §3.1）。
 *  - Service Worker は apps/web/public/sw.js（同一オリジンの /sw.js）。登録はログイン済みのシェルが行い、ここでは取得（無ければ登録）だけ
 *  - 購読 = pushManager.subscribe({ userVisibleOnly, applicationServerKey: VAPID 公開鍵 }) → POST /devices（pushToken = 購読の JSON）
 *  - 「この端末」の判定: 登録した端末 ID を localStorage に持つ（一覧には push_token を出さないため）。消えても再登録で同じ行に戻る（冪等）
 *  - iOS は「ホーム画面に追加」して standalone で開いたときだけ（16.4 以降）。Safari のタブでは PushManager が無い
 */
export const SW_PATH = '/sw.js';
const DEVICE_ID_KEY = 'saas.pushDeviceId';

export type PushUnsupportedReason = 'insecure' | 'no-service-worker' | 'no-push' | 'ios-not-standalone';

export interface PushSupport {
  supported: boolean;
  reason: PushUnsupportedReason | null;
  isIos: boolean;
  standalone: boolean;
}

export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPadOS 13 以降は Macintosh を名乗る。タッチ点数で見分ける
  return /iP(hone|ad|od)/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const nav = navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true || window.matchMedia?.('(display-mode: standalone)').matches === true;
}

export function detectPushSupport(): PushSupport {
  const ios = isIos();
  const standalone = isStandalone();
  if (typeof window === 'undefined') return { supported: false, reason: 'no-push', isIos: ios, standalone };
  if (!window.isSecureContext) return { supported: false, reason: 'insecure', isIos: ios, standalone };
  if (!('serviceWorker' in navigator)) return { supported: false, reason: 'no-service-worker', isIos: ios, standalone };
  if (!('PushManager' in window) || !('Notification' in window)) {
    return { supported: false, reason: ios && !standalone ? 'ios-not-standalone' : 'no-push', isIos: ios, standalone };
  }
  return { supported: true, reason: null, isIos: ios, standalone };
}

/** SW の登録（冪等）。シェルがログイン時に呼ぶ。未対応なら null */
export async function ensureServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    return (await navigator.serviceWorker.getRegistration(SW_PATH)) ?? (await navigator.serviceWorker.register(SW_PATH, { scope: '/' }));
  } catch {
    return null;
  }
}

/**
 * この端末（ブラウザ）の現在の購読。無ければ null。
 * registerIfMissing = false なら SW を新たに登録しない（ログアウト時など、登録が無ければ購読も無いはずの場面）
 */
export async function currentSubscription(opts: { registerIfMissing?: boolean } = {}): Promise<PushSubscription | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  const reg = opts.registerIfMissing === false ? await navigator.serviceWorker.getRegistration(SW_PATH).catch(() => undefined) : await ensureServiceWorker();
  if (!reg) return null;
  try {
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export class PushPermissionDenied extends Error {
  constructor() {
    super('通知がブロックされています');
    this.name = 'PushPermissionDenied';
  }
}

/** 通知の許可を求めて購読する。拒否されたら PushPermissionDenied */
export async function subscribePush(publicKey: string): Promise<PushSubscription> {
  const reg = await ensureServiceWorker();
  if (!reg) throw new Error('この環境では Service Worker を使えません');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new PushPermissionDenied();
  const existing = await reg.pushManager.getSubscription();
  if (existing) return existing;
  return reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource });
}

export const pushDeviceStore = {
  get(): string | null {
    try {
      return localStorage.getItem(DEVICE_ID_KEY);
    } catch {
      return null;
    }
  },
  set(id: string): void {
    try {
      localStorage.setItem(DEVICE_ID_KEY, id);
    } catch {
      /* ignore */
    }
  },
  clear(): void {
    try {
      localStorage.removeItem(DEVICE_ID_KEY);
    } catch {
      /* ignore */
    }
  },
};

/** この端末を台帳に登録する（既にあれば同じ行が返る）。返り値は端末 ID */
export async function registerThisDevice(api: ApiClient, subscription: PushSubscription): Promise<string> {
  const d = await coreApi.registerDevice(api, { platform: 'web', pushToken: JSON.stringify(subscription.toJSON()), capabilities: [] });
  pushDeviceStore.set(d.id);
  return d.id;
}

/**
 * この端末の購読を解く（台帳の行 → ブラウザの購読の順）。ログアウト時にも呼ぶ（共有 PC に通知が届き続けないように）。
 * 台帳の削除に失敗しても（既に消えている等）ブラウザ側は解除する
 */
export async function forgetThisDevice(api: ApiClient | null): Promise<void> {
  const id = pushDeviceStore.get();
  if (id && api) {
    try {
      await coreApi.deleteDevice(api, id);
    } catch {
      /* 既に無い・オフライン: ブラウザ側だけ解除する */
    }
  }
  pushDeviceStore.clear();
  const sub = await currentSubscription({ registerIfMissing: false });
  if (sub) {
    try {
      await sub.unsubscribe();
    } catch {
      /* ignore */
    }
  }
}
